import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setAboutDropdownOpen(false);
  }, [location]);

  // On sub-pages the hero is cream-colored, so always use solid white header + dark text
  const isSolid = scrolled || !isHomePage;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_finance-portal-237/artifacts/51c1ld3v_Logo%20White.png"
              alt="UK Stage"
              className="h-14 w-auto transition-all duration-300"
              style={{
                filter: isSolid
                  ? 'brightness(0) saturate(100%) invert(16%) sepia(6%) saturate(340%) hue-rotate(314deg) brightness(96%) contrast(90%)'
                  : 'brightness(1)'
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {/* About Us Dropdown — transparent bridge removes the gap so mouse never leaves */}
            <div 
              className="relative"
              onMouseEnter={() => setAboutDropdownOpen(true)}
              onMouseLeave={() => setAboutDropdownOpen(false)}
            >
              <button 
                className={`flex items-center text-sm uppercase tracking-widest transition-colors duration-300 ${
                  isSolid ? 'text-[#2A2A2A] hover:text-[#8B7355]' : 'text-white hover:text-[#A08872]'
                }`}
              >
                ABOUT US
                <ChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {aboutDropdownOpen && (
                /* pt-2 here acts as a transparent bridge so the gap doesn't close the menu */
                <div className="absolute top-full left-0 w-48 pt-2">
                  <div className="bg-white shadow-lg py-2 border-t-2 border-[#8B7355]">
                    <Link to="/the-team" className="block px-6 py-3 text-sm text-[#2A2A2A] hover:bg-[#F5F1ED] hover:text-[#8B7355] transition-colors uppercase tracking-wider">
                      THE TEAM
                    </Link>
                    <Link to="/advisory-board" className="block px-6 py-3 text-sm text-[#2A2A2A] hover:bg-[#F5F1ED] hover:text-[#8B7355] transition-colors uppercase tracking-wider">
                      ADVISORY BOARD
                    </Link>
                    <Link to="/join-us" className="block px-6 py-3 text-sm text-[#2A2A2A] hover:bg-[#F5F1ED] hover:text-[#8B7355] transition-colors uppercase tracking-wider">
                      JOIN US
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/how-we-work" 
              className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                isSolid ? 'text-[#2A2A2A] hover:text-[#8B7355]' : 'text-white hover:text-[#A08872]'
              }`}
            >
              HOW WE WORK
            </Link>
            
            <Link 
              to="/portfolio" 
              className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                isSolid ? 'text-[#2A2A2A] hover:text-[#8B7355]' : 'text-white hover:text-[#A08872]'
              }`}
            >
              PORTFOLIO
            </Link>
            
            <Link 
              to="/news" 
              className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                isSolid ? 'text-[#2A2A2A] hover:text-[#8B7355]' : 'text-white hover:text-[#A08872]'
              }`}
            >
              NEWS
            </Link>
            
            <Link 
              to="/social-responsibility" 
              className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                isSolid ? 'text-[#2A2A2A] hover:text-[#8B7355]' : 'text-white hover:text-[#A08872]'
              }`}
            >
              SOCIAL RESPONSIBILITY
            </Link>
          </nav>

          {/* Contact Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button 
                variant="outline"
                className={`transition-all duration-300 ${
                  isSolid
                    ? 'border-[#2A2A2A] text-[#2A2A2A] hover:bg-[#2A2A2A] hover:text-white' 
                    : 'border-white text-white hover:bg-white hover:text-[#2A2A2A]'
                }`}
              >
                CONTACT US
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className={isSolid ? 'text-[#2A2A2A]' : 'text-white'} />
            ) : (
              <Menu className={isSolid ? 'text-[#2A2A2A]' : 'text-white'} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg animate-fade-in">
          <nav className="container mx-auto px-6 py-6 space-y-4">
            <div>
              <button 
                onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                className="flex items-center justify-between w-full text-sm text-[#2A2A2A] uppercase tracking-widest py-2"
              >
                ABOUT US
                <ChevronDown className={`w-4 h-4 transition-transform ${
                  aboutDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              {aboutDropdownOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  <Link to="/the-team" className="block text-sm text-[#3D3D3D] py-2">The Team</Link>
                  <Link to="/advisory-board" className="block text-sm text-[#3D3D3D] py-2">Advisory Board</Link>
                  <Link to="/join-us" className="block text-sm text-[#3D3D3D] py-2">Join Us</Link>
                </div>
              )}
            </div>
            <Link to="/how-we-work" className="block text-sm text-[#2A2A2A] uppercase tracking-widest py-2">HOW WE WORK</Link>
            <Link to="/portfolio" className="block text-sm text-[#2A2A2A] uppercase tracking-widest py-2">PORTFOLIO</Link>
            <Link to="/news" className="block text-sm text-[#2A2A2A] uppercase tracking-widest py-2">NEWS</Link>
            <Link to="/social-responsibility" className="block text-sm text-[#2A2A2A] uppercase tracking-widest py-2">SOCIAL RESPONSIBILITY</Link>
            <Link to="/contact" className="block text-sm text-[#2A2A2A] uppercase tracking-widest py-2 border-t pt-4 mt-4">CONTACT US</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;